import React, { useEffect, useMemo, useState } from 'react';
import ModalTemplateB from '../../../../../components/_template/modal/ModalTemplateB';
import BasicButton from '../../../../../components/_atom/button/BasicButton';
import { ButtonTheme } from '../../../../../components/_atom/button/StyledButton';
import CommentInput from '../../../../../components/_atom/input/CommentInput';
import CopyToClipboard from 'react-copy-to-clipboard';
import { ReactComponent as IconCopy } from '../../../../../assets/images/icon_copy.svg';
import toast from 'react-hot-toast';
import RadioButton from '../../../../../../legacy-common/atom/button/RadioButton';
import { logic } from '../../../../../../logic';
import TagBadge, { TagBadgeType } from '../../TagBadge';
import Select from '../../../../../components/_atom/select/Select';
import { Webhook, WebhookEventType } from '../../../../../assets/constant/webhookManager.type';

const enum CREATE_WEBHOOK_STEP {
  REGISTRATION,
  COMPLETE,
}

type CreateWebhookModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  refreshWebhook?: () => void;
  defaultServiceId?: number;
  defaultKitId?: number;
};
const CreateWebhookModal = (props: CreateWebhookModalProps) => {
  const { isOpen, closeModal, refreshWebhook, defaultServiceId, defaultKitId } = props;
  const [step, setStep] = useState<CREATE_WEBHOOK_STEP>(CREATE_WEBHOOK_STEP.REGISTRATION);
  const [webhook, setWebhook] = useState<Webhook | null>(null);
  const title = useMemo(() => {
    switch (step) {
      case CREATE_WEBHOOK_STEP.REGISTRATION:
        return 'Create Webhook';
      case CREATE_WEBHOOK_STEP.COMPLETE:
        return 'Webhook Creation Success';
    }
  }, [step]);
  const onClosed = () => {
    setStep(CREATE_WEBHOOK_STEP.REGISTRATION);
    setWebhook(null);
    closeModal();
  };
  return (
    <ModalTemplateB isOpen={isOpen} title={title} showDivideLine={false} onRequestClose={onClosed} shouldCloseOnOverlayClick={false}>
      {step === CREATE_WEBHOOK_STEP.REGISTRATION && (
        <CreateWebhookForm onClosed={onClosed} setWebhook={setWebhook} setStep={setStep} defaultServiceId={defaultServiceId} defaultKitId={defaultKitId} />
      )}
      {step === CREATE_WEBHOOK_STEP.COMPLETE && (
        <CompleteCreateWebhook
          onClosed={() => {
            onClosed();
            refreshWebhook && refreshWebhook();
          }}
          webhook={webhook}
        />
      )}
    </ModalTemplateB>
  );
};

export default CreateWebhookModal;

export const enum SERVICE_ID {
  TOKEN_KIT = 1,
  TRANSACTION = 2,
  NFT_KIT = 3,
  ACCOUNT_MANAGER = 4,
}

const SERVICES: {
  id: SERVICE_ID;
  name: string;
  value: 'TOKEN_KIT' | 'TRANSACTION' | 'NFT_KIT' | 'ACCOUNT_MANAGER';
  comingSoon: boolean;
}[] = [
  {
    id: SERVICE_ID.TOKEN_KIT,
    name: 'Token Kit',
    value: 'TOKEN_KIT',
    comingSoon: false,
  },
  {
    id: SERVICE_ID.TRANSACTION,
    name: 'Transaction',
    value: 'TRANSACTION',
    comingSoon: false,
  },
  {
    id: SERVICE_ID.NFT_KIT,
    name: 'NFT Kit',
    value: 'NFT_KIT',
    comingSoon: false,
  },
  {
    id: SERVICE_ID.ACCOUNT_MANAGER,
    name: 'Account Manager',
    value: 'ACCOUNT_MANAGER',
    comingSoon: true,
  },
];
const CreateWebhookForm = (props: {
  onClosed: () => void;
  setWebhook: (webhook: Webhook) => void;
  setStep: (step: CREATE_WEBHOOK_STEP) => void;
  defaultServiceId?: number;
  defaultKitId?: number;
}) => {
  const { onClosed, setWebhook, setStep, defaultServiceId, defaultKitId } = props;
  const [webhookName, setWebhookName] = useState<string>('');
  const [endpointUrl, setEndpointUrl] = useState<string>('');
  const [kits, setKits] = useState<any[]>([]);
  const [eventTypes, setEventTypes] = useState<WebhookEventType[]>([]);

  const [selectedService, setSelectedService] = useState<{
    id: SERVICE_ID;
    name: string;
    value: 'TOKEN_KIT' | 'TRANSACTION' | 'NFT_KIT' | 'ACCOUNT_MANAGER';
    comingSoon: boolean;
  }>(SERVICES[defaultServiceId || 0]);
  useEffect(() => {
    const defaultService = SERVICES.find((service) => service.id === defaultServiceId);
    if (defaultService) {
      setSelectedService(defaultService);
    }
  }, [defaultServiceId]);
  const [selectedKit, setSelectedKit] = useState<any>();
  const getKits = async () => {
    const getUserKits = () => {
      switch (selectedService?.id) {
        case SERVICE_ID.TOKEN_KIT:
          return logic.tokenKit.getUserTokenKits;
        case SERVICE_ID.NFT_KIT:
          return logic.nftKit.getUserNFTKits;
        default:
          return () => ({ result: { rows: [] }, error: null });
      }
    };
    const { result, error } = await getUserKits()({});
    if (error) {
      return [];
    }
    return result?.rows || [];
  };
  const getPublicKits = async () => {
    const getUserPublicKits = () => {
      switch (selectedService?.id) {
        case SERVICE_ID.TOKEN_KIT:
          return logic.tokenKit.getUserPublicTokenKits;
        case SERVICE_ID.NFT_KIT:
          return logic.nftKit.getUserPublicNFTKits;
        default:
          return () => ({ result: { rows: [] }, error: null });
      }
    };
    const { result, error } = await getUserPublicKits()({});
    if (error) {
      return [];
    }
    return result?.rows || [];
  };
  const getKitsForWebhook = async () => {
    const myKits = await getKits();
    const publicKits = await getPublicKits();
    setKits([...myKits, ...publicKits]);
    const defaultKit = [...myKits, ...publicKits].find((kit) => (kit.kit_id ? kit.kit_id === defaultKitId : kit.nft_kit_id === defaultKitId));
    if (defaultKit) {
      setSelectedKit(defaultKit);
    }
  };
  const [selectedEventType, setSelectedEventType] = useState<WebhookEventType>();
  const getEventTypes = async () => {
    const { result, error } = await logic.webhook.getWebhookEventType(selectedService?.value as 'TOKEN_KIT');
    if (error) {
      return;
    }
    setEventTypes(result?.data);
  };
  useEffect(() => {
    if ([SERVICE_ID.TOKEN_KIT, SERVICE_ID.NFT_KIT].includes(selectedService?.id)) {
      getKitsForWebhook();
      getEventTypes();
      setSelectedEventType(undefined);
    }
    if (selectedService?.value === 'TRANSACTION') {
      setSelectedEventType({
        event_id: -1,
        service_identifier: 'TRANSACTION',
        event_name: 'TRANSACTION',
        event_description: 'Transfer event',
        created_at: '',
        updated_at: '',
      });
    }
    setSelectedKit(undefined);
  }, [selectedService]);

  const canCreateWebhook = useMemo(() => {
    if ([SERVICE_ID.TOKEN_KIT, SERVICE_ID.NFT_KIT].includes(selectedService?.id)) {
      return webhookName && endpointUrl && selectedKit && selectedEventType;
    }
    if (selectedService?.value === 'TRANSACTION') {
      return webhookName && endpointUrl && selectedEventType;
    }
  }, [webhookName, endpointUrl, selectedKit, selectedEventType]);
  const createWebhook = async () => {
    if (!canCreateWebhook) {
      return;
    }
    const resourceId = selectedKit?.kit_id || selectedKit?.nft_kit_id;
    const { result, error } = await logic.webhook.subscribeWebhook(endpointUrl, webhookName, selectedEventType?.event_name as any, {
      resourceIdentifier: resourceId,
    });
    if (error) {
      if (error.data === 'subscription_already_exists') {
        toast.error('Webhook already exists');
        return;
      }
      toast.error('Failed to create webhook');
      return;
    }
    if (result) {
      setWebhook(result);
      setStep(CREATE_WEBHOOK_STEP.COMPLETE);
    }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 40, paddingLeft: 10, width: 652 }}>
      <div className="kit-label-value-wrapper column">
        <div className="kit-label">
          Webhook Name
          <div className="kit-label required">*</div>
        </div>
        <CommentInput
          value={webhookName}
          style={{ paddingLeft: 10 }}
          placeholder={'Enter the name of the webhook'}
          onChange={(e) => {
            setWebhookName(e.target.value.trimStart());
          }}
        />
      </div>
      <div className="kit-label-value-wrapper column">
        <div className="kit-label">
          Endpoint URL
          <div className="kit-label required">*</div>
        </div>
        <CommentInput
          value={endpointUrl}
          style={{ paddingLeft: 10 }}
          placeholder={'https://'}
          onChange={(e) => {
            setEndpointUrl(e.target.value.trimStart());
          }}
        />
      </div>
      <div className="kit-label-value-wrapper column">
        <div className="kit-label">
          Webhook type
          <div className="kit-label required">*</div>
        </div>
        <div className="kit-label-description">Select the service and event type to receive events</div>
        <div className="kit-label-value-wrapper column sub" style={{ marginBottom: 10 }}>
          <div className="kit-label sub">Service</div>
          <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: 5, gap: 12 }}>
            {SERVICES.map((service) => (
              <div
                onClick={() => {
                  if (service.comingSoon) {
                    return;
                  }
                  setSelectedService(service);
                }}
                key={service.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: service.comingSoon ? 'default' : 'pointer',
                }}
              >
                <RadioButton checked={service === selectedService} readOnly labelStyle={{ padding: '0 10px' }} disabled={service.comingSoon} />
                <div>{service.name}</div>
                {service.comingSoon && <TagBadge type={TagBadgeType.Normal} text={'Coming Soon'} />}
              </div>
            ))}
            {[SERVICE_ID.TOKEN_KIT, SERVICE_ID.NFT_KIT].includes(selectedService?.id) && (
              <Select
                options={kits}
                labelKey={'name'}
                valueKey={null}
                selectedValue={selectedKit}
                setSelectedValue={setSelectedKit}
                maxHeight={200}
                placeholder={'Select the kit to receive'}
              />
            )}
          </div>
        </div>
        {[SERVICE_ID.TOKEN_KIT, SERVICE_ID.NFT_KIT].includes(selectedService?.id) && (
          <div className="kit-label-value-wrapper column sub">
            <div className="kit-label sub">Event type</div>
            <Select
              labelKey={'event_name'}
              valueKey={null}
              options={eventTypes}
              selectedValue={selectedEventType}
              setSelectedValue={setSelectedEventType}
              placeholder={'Select the type of event to receive'}
            />
          </div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10 }}>
        <BasicButton theme={ButtonTheme.NORMAL} onClick={onClosed}>
          Close
        </BasicButton>
        <BasicButton onClick={createWebhook} disabled={!canCreateWebhook}>
          Create
        </BasicButton>
      </div>
    </div>
  );
};

const CompleteCreateWebhook = (props: { onClosed: () => void; webhook: Webhook | null }) => {
  const { onClosed, webhook } = props;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 40, paddingLeft: 10, width: 652 }}>
      <div className="kit-label-value-wrapper column">
        <div className="kit-label">Webhook Name</div>
        <div className="kit-value" style={{ paddingLeft: 10 }}>
          {webhook?.name}
        </div>
      </div>
      <div className="kit-label-value-wrapper column">
        <div className="kit-label">Endpoint URL</div>
        <div className="kit-value" style={{ paddingLeft: 10, gap: 10 }}>
          {webhook?.webhook_url}
          <CopyToClipboard text={webhook?.webhook_url} onCopy={() => toast('Endpoint URL Copied', { icon: '👏' })}>
            <IconCopy style={{ minWidth: 20, minHeight: 20, objectFit: 'contain', cursor: 'pointer' }} className="icon-copy" />
          </CopyToClipboard>
        </div>
      </div>
      <div className="kit-label-value-wrapper column">
        <div className="kit-label">Service</div>
        <div className="kit-value" style={{ paddingLeft: 10 }}>
          {webhook?.resource_subscriptions[0]?.service_identifier === 'TOKEN_KIT' ? 'Token Kit' : 'Transaction'}
        </div>
      </div>
      <div className="kit-label-value-wrapper column">
        <div className="kit-label">Event Type</div>
        <div className="kit-value" style={{ paddingLeft: 10 }}>
          {webhook?.event_name}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10 }}>
        <BasicButton theme={ButtonTheme.PRIMARY} onClick={onClosed}>
          Confirm
        </BasicButton>
      </div>
    </div>
  );
};
